import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { ParticipantLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { 
  Lightbulb, AlertTriangle, Sparkles, Plus, Clock, CheckCircle, XCircle, 
  Loader2, MessageCircle, Send
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  verbesserung: { label: "Verbesserung", icon: Lightbulb, color: "bg-blue-500/10 text-blue-500 border-blue-500/30" },
  problem: { label: "Problem", icon: AlertTriangle, color: "bg-red-500/10 text-red-500 border-red-500/30" },
  idee: { label: "Idee", icon: Sparkles, color: "bg-purple-500/10 text-purple-500 border-purple-500/30" }
};

const STATUS_CONFIG = {
  neu: { label: "Neu", icon: Clock, color: "bg-gray-500/10 text-gray-400 border-gray-500/30" },
  in_bearbeitung: { label: "In Bearbeitung", icon: Loader2, color: "bg-yellow-500/10 text-yellow-500 border-yellow-500/30" },
  umgesetzt: { label: "Umgesetzt", icon: CheckCircle, color: "bg-green-500/10 text-green-500 border-green-500/30" },
  abgelehnt: { label: "Abgelehnt", icon: XCircle, color: "bg-red-500/10 text-red-500 border-red-500/30" }
};

const KVP = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState({ categories: [], statuses: [], areas: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedEntry, setSelectedEntry] = useState(null);
  
  // Dialogs
  const [newDialog, setNewDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  
  // Form
  const [form, setForm] = useState({ title: "", description: "", category: "verbesserung", area: "", priority: "mittel" });
  const [commentText, setCommentText] = useState("");

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      const [entriesRes, catsRes] = await Promise.all([
        axios.get(`${API}/kvp`, { params: activeTab !== "all" ? { status: activeTab } : {} }),
        axios.get(`${API}/kvp/categories`)
      ]);
      setEntries(entriesRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.title || !form.description) {
      toast.error("Bitte Titel und Beschreibung eingeben");
      return;
    }
    try {
      await axios.post(`${API}/kvp`, null, {
        params: {
          title: form.title,
          description: form.description,
          category: form.category,
          area: form.area || undefined,
          priority: form.priority
        }
      });
      toast.success("Vorschlag eingereicht!");
      setNewDialog(false);
      setForm({ title: "", description: "", category: "verbesserung", area: "", priority: "mittel" });
      loadData();
    } catch (error) {
      toast.error("Fehler beim Erstellen");
    }
  };

  const openDetail = async (entryId) => {
    try {
      const res = await axios.get(`${API}/kvp/${entryId}`);
      setSelectedEntry(res.data);
      setDetailDialog(true);
    } catch (error) {
      toast.error("Fehler beim Laden");
    }
  };

  const handleComment = async () => {
    if (!commentText.trim() || !selectedEntry) return;
    try {
      await axios.post(`${API}/kvp/${selectedEntry.id}/comment`, null, {
        params: { content: commentText }
      });
      toast.success("Kommentar gesendet");
      setCommentText("");
      const res = await axios.get(`${API}/kvp/${selectedEntry.id}`);
      setSelectedEntry(res.data);
    } catch (error) {
      toast.error("Fehler");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric"
    });
  };

  const countByStatus = (status) => entries.filter(e => e.status === status).length;

  return (
    <ParticipantLayout title="Verbesserungsvorschläge">
      <div className="space-y-6" data-testid="kvp-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div>
            <p className="text-muted-foreground">
              Teilen Sie Ihre Ideen und Verbesserungsvorschläge mit uns.
            </p>
          </div>
          <Button onClick={() => setNewDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Neuer Vorschlag
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(STATUS_CONFIG).map(([status, config]) => {
            const Icon = config.icon;
            return (
              <Card 
                key={status} 
                className={`bg-card border-border cursor-pointer transition-colors ${activeTab === status ? "ring-2 ring-primary" : ""}`}
                onClick={() => setActiveTab(status)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{countByStatus(status)}</p>
                      <p className="text-xs text-muted-foreground">{config.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Tabs & List */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">Alle ({entries.length})</TabsTrigger>
            <TabsTrigger value="neu">Neu</TabsTrigger>
            <TabsTrigger value="in_bearbeitung">In Bearbeitung</TabsTrigger>
            <TabsTrigger value="umgesetzt">Umgesetzt</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : entries.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Lightbulb className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Keine Einträge</p>
                  <Button className="mt-4" onClick={() => setNewDialog(true)}>
                    Ersten Vorschlag erstellen
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {entries.filter(e => activeTab === "all" || e.status === activeTab).map(entry => {
                  const catConfig = CATEGORY_CONFIG[entry.category];
                  const statusConfig = STATUS_CONFIG[entry.status];
                  const CatIcon = catConfig?.icon || Lightbulb;
                  const StatusIcon = statusConfig?.icon || Clock;
                  
                  return (
                    <Card 
                      key={entry.id} 
                      className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer"
                      onClick={() => openDetail(entry.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-lg ${catConfig?.color || "bg-muted"} flex items-center justify-center flex-shrink-0`}>
                            <CatIcon className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="font-medium">{entry.title}</h3>
                              <Badge className={statusConfig?.color || "bg-muted"}>
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {statusConfig?.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2">{entry.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span>{formatDate(entry.created_at)}</span>
                              {entry.area && <span>Bereich: {entry.area}</span>}
                              <span className="flex items-center gap-1">
                                <MessageCircle className="h-3 w-3" />
                                {entry.comment_count} Kommentare
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* New Entry Dialog */}
        <Dialog open={newDialog} onOpenChange={setNewDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Neuer Verbesserungsvorschlag</DialogTitle>
              <DialogDescription>Beschreiben Sie Ihre Idee oder melden Sie ein Problem.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Kategorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="verbesserung">Verbesserungsvorschlag</SelectItem>
                    <SelectItem value="problem">Problem melden</SelectItem>
                    <SelectItem value="idee">Neue Idee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Bereich (optional)</Label>
                <Select value={form.area} onValueChange={(v) => setForm({ ...form, area: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Bereich auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.areas?.map(area => (
                      <SelectItem key={area} value={area}>{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Titel *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Kurze Zusammenfassung"
                />
              </div>
              <div className="space-y-2">
                <Label>Beschreibung *</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Beschreiben Sie Ihren Vorschlag im Detail..."
                  rows={5}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setNewDialog(false)}>Abbrechen</Button>
              <Button onClick={handleCreate}>Einreichen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
            {selectedEntry && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={CATEGORY_CONFIG[selectedEntry.category]?.color}>
                      {CATEGORY_CONFIG[selectedEntry.category]?.label}
                    </Badge>
                    <Badge className={STATUS_CONFIG[selectedEntry.status]?.color}>
                      {STATUS_CONFIG[selectedEntry.status]?.label}
                    </Badge>
                    {selectedEntry.area && <Badge variant="outline">{selectedEntry.area}</Badge>}
                  </div>
                  <DialogTitle className="text-xl mt-2">{selectedEntry.title}</DialogTitle>
                  <div className="text-sm text-muted-foreground">
                    Eingereicht am {formatDate(selectedEntry.created_at)}
                  </div>
                </DialogHeader>

                <div className="py-4 border-b border-border">
                  <p className="whitespace-pre-wrap">{selectedEntry.description}</p>
                </div>

                {selectedEntry.admin_comment && (
                  <div className="py-4 border-b border-border bg-secondary/30 rounded-lg p-4 -mx-2">
                    <p className="text-sm font-medium mb-1">Admin-Antwort:</p>
                    <p className="text-sm text-muted-foreground">{selectedEntry.admin_comment}</p>
                  </div>
                )}

                {/* Comments */}
                <div className="space-y-4 py-4">
                  <h4 className="font-medium">{selectedEntry.comments?.length || 0} Kommentare</h4>
                  
                  {selectedEntry.comments?.map(comment => (
                    <div key={comment.id} className="p-3 rounded-lg bg-secondary/30">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{comment.author?.name}</span>
                          {comment.author?.role !== "participant" && (
                            <Badge variant="outline" className="text-xs">{comment.author?.role}</Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">{formatDate(comment.created_at)}</span>
                      </div>
                      <p className="text-sm">{comment.content}</p>
                    </div>
                  ))}

                  {/* Add Comment */}
                  <div className="flex gap-2">
                    <Textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Kommentar hinzufügen..."
                      rows={2}
                      className="flex-1"
                    />
                    <Button onClick={handleComment} disabled={!commentText.trim()}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </ParticipantLayout>
  );
};

export default KVP;
